import { FullMetadata } from '@firebase/storage';
import {
  addDoc,
  arrayRemove,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { z } from 'zod';

import { ICard } from 'types/card';
import { ICompany, ICompanyResponse, ISanitizedCompany } from 'types/company';
import { IStory } from 'types/story';
import { ITeam } from 'types/team';
import {
  EventTypes,
  StoryViewCompleteTrackingEvent,
  StoryViewStartTrackingEvent,
} from 'types/tracking';
import { IUserResponse } from 'types/user';

import { sendRegisterMails } from 'services/api/firebase/callables';
import { defaultCardsFactory } from 'services/cards';
import { collectionFactory } from 'services/collection';
import { storyFactory } from 'services/story';
import { storyFromFileFactory } from 'services/storyFromFile';
import { teamFactory } from 'services/team';

import { db } from 'clients/firebase';

import { duplicateCardItems } from './client/duplicate';
import {
  clientCardConverter,
  clientCollectionConverter,
  clientIdConverter,
  clientStoryConverter,
  clientTeamConverter,
} from './converters';

export const createCollection = async ({
  company,
  user,
  name,
  emoji,
}: {
  company: ISanitizedCompany;
  user: IUserResponse;
  name: string;
  emoji: string;
}) => {
  const initialData = collectionFactory({ company, user, name, emoji });

  const collectionsRef = collection(db, 'Collections');
  const collectionRef = await (
    await addDoc(collectionsRef, initialData)
  ).withConverter(clientCollectionConverter);

  return { collectionId: collectionRef.id };
};

export const updateCollectionInfo = async ({
  name,
  emoji,
  sorting,
  collectionId,
}: {
  name: string;
  emoji: string;
  sorting?: string[];
  collectionId: string;
}) => {
  const collectionRef = doc(db, `Collections/${collectionId}`);
  let writeObject = sorting
    ? { name, emoji, position: sorting }
    : { name, emoji };
  await setDoc(collectionRef, writeObject, { merge: true });

  return { collectionId: collectionRef.id };
};

export const updateCollectionPublish = async ({
  publish,
  collectionId,
}: {
  publish: boolean;
  collectionId: string;
}) => {
  const collectionRef = doc(db, `Collections/${collectionId}`);
  let publishData = {
    meta: { publishedAt: publish ? new Date(Date.now()) : null },
  };
  await setDoc(collectionRef, publishData, { merge: true });

  return { collectionId: collectionRef.id };
};

type LogoData = {
  logoPath?: string;
  logoDarkPath?: string;
  logoQRPath?: string;
};

// Returns true if another company already uses this slug. Used to give the
// admin immediate feedback before saving a Company URL change; the
// onCompanyUpdate Cloud Function is the authoritative backstop.
export const isCompanySlugTaken = async (
  slug: string,
  excludeCompanyId: string,
) => {
  const snapshot = await getDocs(
    query(collection(db, 'Companies'), where('slug', '==', slug)),
  );

  return snapshot.docs.some(doc => doc.id !== excludeCompanyId);
};

// The settings the company drawer edits. Unknown keys are kept; the secrets in
// `integrations` are not part of it and are never read into the form.
const companySettingsSchema = z.looseObject({
  name: z.string(),
  slug: z.string(),
  domain: z.string().default(''),
  logoPath: z.string().default(''),
  logoDarkPath: z.string().default(''),
  logoQRPath: z.string().nullable().default(null),
  private: z.boolean().default(false),
  galleryAccessKey: z.string().optional(),
  theming: z
    .looseObject({
      colors: z.array(z.string()),
      gradients: z.array(z.string()).default([]),
    })
    .default({ colors: [], gradients: [] }),
});

export type CompanySettings = z.infer<typeof companySettingsSchema> & {
  id: string;
};

// Read on demand (e.g. when the settings drawer opens), so the form is seeded
// from Firestore and not from a server-rendered prop that may be cached. null
// when the company is missing or malformed.
export const getCompany = async (
  companyId: string,
): Promise<CompanySettings | null> => {
  const snapshot = await getDoc(doc(db, `Companies/${companyId}`));

  if (!snapshot.exists()) {
    return null;
  }

  const result = companySettingsSchema.safeParse(snapshot.data());

  if (!result.success) {
    console.error(`Malformed company ${snapshot.ref.path}`, result.error);
    return null;
  }

  // After the spread: a stored `id` field must not replace the snapshot id.
  return { ...result.data, id: snapshot.id };
};

export const updateCompany = async ({
  companyId,
  values,
  colors,
  logoPathLight,
  logoPathDark,
  logoPathQR,
}: {
  companyId: string;
  values: Partial<ICompany>;
  // Omitted when the colors did not change, so `theming` is not written.
  colors?: string[];
  logoPathLight?: string;
  logoPathDark?: string;
  logoPathQR?: string;
}) => {
  const companyRef = doc(db, `Companies/${companyId}`);

  let logoData: LogoData | null;
  if (logoPathLight || logoPathDark || logoPathQR) {
    logoData = {};
    if (logoPathLight) {
      logoData.logoPath = logoPathLight;
    }
    if (logoPathDark) {
      logoData.logoDarkPath = logoPathDark;
    }
    if (logoPathQR) {
      logoData.logoQRPath = logoPathQR;
    }
  } else {
    logoData = null;
  }

  await setDoc(
    companyRef,
    {
      ...values,
      ...(colors && { theming: { colors } }),
      ...logoData,
    },
    { merge: true },
  );

  return { companyId: companyRef.id };
};

// arrayRemove is applied server-side, so concurrent removals from the same
// team can't overwrite each other. updateDoc rejects if the team is missing.
export const removeUserFromTeam = async (userId: string, teamId: string) => {
  const teamRef = doc(db, `Teams/${teamId}`);
  await updateDoc(teamRef, { users: arrayRemove(userId) });
};

export const getCollectionById = async (collectionId: string) => {
  const collectionRef = doc(db, `Collections/${collectionId}`).withConverter(
    clientCollectionConverter,
  );

  const collectionSnapshot = await getDoc(collectionRef);

  if (!collectionSnapshot.exists()) {
    throw new Error(`Collection with ID ${collectionId} does not exist`);
  }

  const collectionData = collectionSnapshot.data();

  return { collectionId: collectionSnapshot.id, ...collectionData };
};

export const updateCompanyExternalEmails = async (
  companyId: string,
  emails: string,
) => {
  let emailsToAdd = emails
    .split(/\r?\n/)
    .map(v => v.trim().toLowerCase())
    .filter(Boolean);
  emailsToAdd = [...new Set(emailsToAdd)];
  const companyRef = doc(db, `Companies/${companyId}`);
  const companySnapshot = await getDoc(companyRef);

  if (!companySnapshot.exists()) {
    throw new Error(`Company with ID ${companyId} does not exist`);
  }

  const company = companySnapshot.data() as ICompanyResponse;
  let companyExternalEmails = company.emails.map(it => it.toLowerCase()) ?? [];
  const emailsToAddFiltered = emailsToAdd.filter(
    email => !companyExternalEmails.includes(email),
  );
  for (const emailToAdd of emailsToAddFiltered) {
    companyExternalEmails.push(emailToAdd);
  }

  if (emailsToAdd.length > 0) {
    await sendRegisterMails({
      companyId: companyId,
      emails: emailsToAdd,
    });
  }

  // Hint: always update to lower the emails, caused of not lowercase import before
  await setDoc(companyRef, { emails: companyExternalEmails }, { merge: true });
};

export const deleteCollection = async (
  collectionId: string,
  companyId: string,
) => {
  const collectionRef = doc(db, `Collections/${collectionId}`).withConverter(
    clientCollectionConverter,
  );

  const batch = writeBatch(db);

  const storiesInCollection = await getStoriesInCollection(
    collectionId,
    companyId,
  );

  for (let story of storiesInCollection) {
    const storyRef = doc(db, `Stories/${story.id}`).withConverter(
      clientStoryConverter,
    );

    const cardsRef = collection(
      db,
      `Stories/${storyRef.id}/Cards`,
    ).withConverter(clientCardConverter);
    let cardsDocs = (await getDocs(cardsRef)).docs;

    cardsDocs.forEach(cardDoc => batch.delete(cardDoc.ref));
    batch.delete(storyRef);
  }
  batch.delete(collectionRef);
  await batch.commit();
};

export const getStory = async (storyId: string) => {
  // Independent reads — run concurrently rather than round-tripping twice in
  // sequence for the same story.
  const [storyDoc, cardsDocs] = await Promise.all([
    getDoc(doc(db, `Stories/${storyId}`).withConverter(clientStoryConverter)),
    getDocs(
      query(
        collection(db, `Stories/${storyId}/Cards`).withConverter(
          clientIdConverter,
        ),
        orderBy('meta.order', 'asc'),
      ),
    ),
  ]);

  const story = storyDoc.data() as IStory;
  const cards = cardsDocs.docs.map(card => card.data() as ICard);

  return { ...story, cards };
};

export const getStoriesInCollection = async (
  collectionId: string,
  companyId: string,
) => {
  const stories = (
    await getDocs(
      query(
        collection(db, 'Stories').withConverter(clientStoryConverter),
        where('meta.companyId', '==', companyId),
        where('meta.collectionId', '==', collectionId),
      ),
    )
  ).docs.map(story => story.data() as IStory);

  return stories;
};

export const getStoriesWithTeam = async (team: ITeam) => {
  const stories = (
    await getCountFromServer(
      query(
        collection(db, 'Stories'),
        where('meta.companyId', '==', team.companyId),
        where('meta.team', 'array-contains', team.id),
      ),
    )
  ).data().count;

  return stories;
};

export const getCollectionsWithTeam = async (team: ITeam) => {
  const collections = (
    await getCountFromServer(
      query(
        collection(db, 'Collections'),
        where('meta.companyId', '==', team.companyId),
        where('meta.team', 'array-contains', team.id),
      ),
    )
  ).data().count;

  return collections;
};

export const createStory = async ({
  company,
  userId,
  collectionId,
}: {
  company: ISanitizedCompany;
  userId: string;
  collectionId: string;
}) => {
  const initialData = storyFactory({ company, userId, collectionId });
  const cards: ICard[] = defaultCardsFactory({ company });

  // The story must be committed before its cards, not in the same batch: the
  // Cards rule resolves the parent story with `get()`, which only sees state
  // from before the batch, so non–Super Admins get denied (#632).
  const storyRef = doc(collection(db, 'Stories'));
  await setDoc(storyRef, initialData);

  const batch = writeBatch(db);
  const cardsRef = collection(db, `Stories/${storyRef.id}/Cards`);

  cards.forEach(card => {
    const cardRef = doc(cardsRef);
    batch.set(cardRef, card);
  });

  await batch.commit();

  return { storyId: storyRef.id };
};

export const updateStory = async ({ story }: { story: IStory }) => {
  const storyRef = doc(db, `Stories/${story.id}`).withConverter(
    clientStoryConverter,
  );
  await setDoc(storyRef, story, { mergeFields: ['title', 'meta'] });

  const batch = writeBatch(db);
  const cardsRef = collection(db, `Stories/${storyRef.id}/Cards`).withConverter(
    clientCardConverter,
  );

  let cardsToDelete = (await getDocs(cardsRef)).docs;
  story.cards.forEach(({ id, ...cardData }, index) => {
    const cardRef = doc(cardsRef, id);
    cardsToDelete = cardsToDelete.filter(cardRef => cardRef.id !== id);
    batch.set(
      cardRef,
      {
        ...cardData,
        meta: {
          ...cardData.meta,
          order: index,
        },
      },
      { merge: true },
    );
  });
  cardsToDelete.forEach(cardRef => {
    batch.delete(cardRef.ref);
  });
  await batch.commit();
};

export const updateStoryCollection = async ({
  storyId,
  collectionId,
}: {
  storyId: string;
  collectionId?: string;
}) => {
  const storyRef = doc(db, `Stories/${storyId}`);
  await setDoc(
    storyRef,
    { meta: { collectionId: collectionId ? collectionId : '' } },
    { merge: true },
  );
};

export const deleteStory = async ({ storyId }: { storyId: string }) => {
  const storyRef = doc(db, `Stories/${storyId}`).withConverter(
    clientStoryConverter,
  );

  const cardsRef = collection(db, `Stories/${storyRef.id}/Cards`).withConverter(
    clientCardConverter,
  );
  let cardsDocs = (await getDocs(cardsRef)).docs;

  const batch = writeBatch(db);
  cardsDocs.forEach(cardDoc => batch.delete(cardDoc.ref));
  batch.delete(storyRef);
  await batch.commit();
};

export const deleteTeam = async (teamId: string) => {
  const teamRef = doc(db, `Teams/${teamId}`);

  try {
    await deleteDoc(teamRef);
  } catch (error) {
    throw new Error('Failed to delete team.');
  }
};

export const deleteUser = async (userId: string) => {
  const userRef = doc(db, `Users/${userId}`);

  try {
    await deleteDoc(userRef);
  } catch (error) {
    throw new Error(`Failed to delete user`);
  }
};

export const duplicateStory = async (storyId: string, userId: string) => {
  const storyDoc = await getDoc(doc(db, `Stories/${storyId}`));

  if (storyDoc.exists()) {
    const storyData = storyDoc.data() as IStory;

    const cardDocs = (await getDocs(collection(db, `Stories/${storyId}/Cards`)))
      .docs;

    const duplicateStoryDoc = await addDoc(
      collection(db, 'Stories').withConverter(clientStoryConverter),
      {
        ...storyData,
        meta: {
          ...storyData.meta,
          userId,
          createdAt: new Date() as unknown as string,
          updatedAt: null,
          publishedAt: null,
        },
        posters: null,
      },
    );
    const cardsRef = collection(db, `Stories/${duplicateStoryDoc.id}/Cards`);

    await Promise.all(
      cardDocs.map(async cardDoc => {
        const cardData = cardDoc.data() as ICard;

        cardData.items = await duplicateCardItems({
          items: cardData.items,
          storyId,
          newStoryId: duplicateStoryDoc.id,
        });

        return addDoc(cardsRef, cardData);
      }),
    );

    return { storyId: duplicateStoryDoc.id };
  }
};

export const createStoryFromFile = async ({
  company,
  user,
  meta,
  link,
  style,
}: {
  company: ISanitizedCompany;
  user: IUserResponse;
  meta: FullMetadata;
  link: string;
  style: string;
}) => {
  const initialData = storyFromFileFactory(user, company, meta, link, style);

  const storiesFromFilesRef = collection(db, 'StoryFromFile');
  const storyFromFileRef = await (
    await addDoc(storiesFromFilesRef, initialData)
  ).withConverter(clientCollectionConverter);

  return { storyFromFileId: storyFromFileRef.id };
};

export const createTeam = async (
  name: string,
  emoji: string,
  userIds: string[],
  companyId: string,
) => {
  const initialData = teamFactory(name, emoji, userIds, companyId);
  const teamsRef = collection(db, 'Teams');
  const teamRef = await (
    await addDoc(teamsRef, initialData)
  ).withConverter(clientTeamConverter);

  return { teamId: teamRef.id };
};

export const updateTeam = async (
  teamId: string,
  name: string,
  emoji: string,
  users: string[],
) => {
  const teamRef = doc(db, `Teams/${teamId}`);

  await setDoc(teamRef, { name, emoji, users }, { merge: true });

  return { teamId };
};

export const deleteCompany = async (companyId: string) => {
  const companyRef = doc(db, `Companies/${companyId}`);

  try {
    await deleteDoc(companyRef);
  } catch (error) {
    throw new Error('Failed to delete company');
  }
};

export const getMostViewedStories = async (
  companySlug: string,
  gte: Date,
  lte: Date,
) => {
  try {
    const eventsSnapshot = await getDocs(
      query(
        collection(db, 'Events'),
        where('data.meta.companySlug', '==', companySlug),
        where('data.meta.createdAt', '>=', gte),
        where('data.meta.createdAt', '<=', lte),
        where('name', '==', EventTypes.STORY_VIEW_START),
      ),
    );

    const events = eventsSnapshot.docs.map(doc => doc.data()) as (
      | StoryViewStartTrackingEvent
      | StoryViewCompleteTrackingEvent
    )[];

    const storyViews = new Map<string, number>();

    events.forEach(event => {
      const { storyId } = event.data;
      storyViews.set(storyId, (storyViews.get(storyId) || 0) + 1);
    });

    const storyIds = Array.from(storyViews.keys()).map(storyId => storyId);

    const stories: IStory[] = [];

    const docPromises = storyIds.map(storyId =>
      getDoc(doc(db, `Stories/${storyId}`).withConverter(clientStoryConverter))
        .then(storyDoc => {
          if (storyDoc.exists()) {
            stories.push(storyDoc.data() as IStory);
          }
        })
        .catch(() => {}),
    );

    await Promise.all(docPromises);

    const storiesWithViews = stories.map(story => ({
      story,
      views: storyViews.get(story.id) || 0,
    }));

    storiesWithViews.sort((a, b) => b.views - a.views);

    return storiesWithViews;
  } catch {
    return [];
  }
};
